/**
 * Comments view — S011 production UI.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2510
 *
 * Accessibility:
 * - Back control: accessible via standard button semantics
 * - Like buttons: aria-label with "Нравится" + count
 * - Send button: aria-disabled when not actionable
 * - Composer input: aria-label for placeholder content
 * - Submission failure region: role="alert" so screen readers announce it
 * - Not-found and error states are distinct from transient loading
 *
 * Security posture:
 * - The view never reads or renders the raw exception message
 *   (model.error). Only a closed set of generic Russian copy is rendered
 *   in the error state. The exception string is reserved for audit logs
 *   outside the user-visible surface.
 */
import type { CommentsModel, AuthorSummary, Comment, RootPost } from './model';
import { formatRelativeTime } from './formatRelativeTime';
import './Comments.css';

const SCREEN_TITLE = 'Комментарии';
const COMPOSER_PLACEHOLDER = 'Написать комментарий...';
const BACK_ARIA_LABEL = 'Назад';
const SEND_ARIA_LABEL = 'Отправить';
const LIKE_ARIA_LABEL = 'Нравится';

const SUBMIT_ERROR_MESSAGE = 'Не удалось отправить комментарий. Попробуйте ещё раз.';
const EMPTY_COMMENTS_MESSAGE = 'Пока никто не оставил комментариев. Будьте первым.';
const LOADING_ARIA_LABEL = 'Загрузка комментариев';
const NOT_FOUND_TITLE = 'Запись не найдена';
const NOT_FOUND_HINT = 'Возможно, она была удалена или ссылка устарела.';
const ERROR_TITLE = 'Не удалось загрузить комментарии';
const ERROR_HINT = 'Проверьте подключение к интернету и попробуйте ещё раз.';

/**
 * Avatar color classes based on initials.
 * Figma uses distinct colors: #8fa7f5 (Mikhail), #f5a0a0 (Olga), #b0b6be (Anonymous)
 */
function getAvatarClass(initials: string): string {
  if (initials === 'М') return 'comments-avatar-comment comments-avatar-michael';
  if (initials === 'О') return 'comments-avatar-comment comments-avatar-olga';
  if (initials === 'А') return 'comments-avatar-comment comments-avatar-anonymous';
  return 'comments-avatar-comment';
}

// =============================================================================
// Component interfaces
// =============================================================================

export interface CommentsViewProps {
  model: CommentsModel;
  referenceTime: Date;
  onBack: () => void;
  onDraftChange: (draft: string) => void;
  onSend: () => void;
  onLike: (commentId: string) => void;
}

// =============================================================================
// Avatar component
// =============================================================================

interface AvatarProps {
  initials: string;
  className: string;
  sizeClass: string;
}

function Avatar({ initials, className, sizeClass }: AvatarProps): JSX.Element {
  return (
    <div
      className={`${sizeClass} ${className}`}
      aria-hidden="true"
      data-testid="comment-avatar"
    >
      {initials}
    </div>
  );
}

// =============================================================================
// Author name + age display
// =============================================================================

interface AuthorDisplayProps {
  author: AuthorSummary;
}

function AuthorDisplay({ author }: AuthorDisplayProps): JSX.Element {
  return (
    <span className={author.isAnonymous ? 'comments-author-name' : 'comments-author-name-inline'}>
      {author.displayName}
      {!author.isAnonymous && author.age != null && (
        <span className="comments-author-age"> • {author.age}</span>
      )}
    </span>
  );
}

// =============================================================================
// Relative time display
// =============================================================================

interface TimeDisplayProps {
  timestamp: string;
  referenceTime: Date;
}

function TimeDisplay({ timestamp, referenceTime }: TimeDisplayProps): JSX.Element {
  const relativeTime = formatRelativeTime(timestamp, referenceTime);
  return (
    <span className="comments-time" data-timestamp={timestamp}>
      {relativeTime}
    </span>
  );
}

// =============================================================================
// Like button
// =============================================================================

interface LikeButtonProps {
  count: number;
  liked: boolean;
  onClick: () => void;
  disabled: boolean;
}

function LikeButton({ count, liked, onClick, disabled }: LikeButtonProps): JSX.Element {
  return (
    <button
      type="button"
      className="comments-like-btn"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${LIKE_ARIA_LABEL} ${count}`}
      aria-pressed={liked}
      data-testid="comment-like-btn"
    >
      <svg
        className="comments-like-icon"
        viewBox="0 0 24 24"
        fill={liked ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
      <span className="comments-like-count">{count}</span>
    </button>
  );
}

// =============================================================================
// Root post display
// =============================================================================

interface RootPostDisplayProps {
  post: RootPost;
  referenceTime: Date;
}

function RootPostDisplay({ post, referenceTime }: RootPostDisplayProps): JSX.Element {
  return (
    <article className="comments-root-post" data-testid="root-post">
      <div className="comments-root-header">
        <Avatar
          initials={post.author.initials}
          className="comments-avatar-root"
          sizeClass=""
        />
        <div className="comments-author-info">
          <AuthorDisplay author={post.author} />
          <TimeDisplay timestamp={post.createdAt} referenceTime={referenceTime} />
        </div>
      </div>
      <p className="comments-root-body">{post.body}</p>
    </article>
  );
}

// =============================================================================
// Comment item display
// =============================================================================

interface CommentItemProps {
  comment: Comment;
  onLike: (commentId: string) => void;
  disabled: boolean;
  referenceTime: Date;
}

function CommentItem({ comment, onLike, disabled, referenceTime }: CommentItemProps): JSX.Element {
  return (
    <article className="comments-comment-item" data-testid="comment-item">
      <div className="comments-comment-header">
        <Avatar
          initials={comment.author.initials}
          className={getAvatarClass(comment.author.initials)}
          sizeClass=""
        />
        <div className="comments-comment-body">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', flexWrap: 'wrap' }}>
            <AuthorDisplay author={comment.author} />
            <TimeDisplay timestamp={comment.createdAt} referenceTime={referenceTime} />
          </div>
          <p className="comments-comment-text">{comment.body}</p>
          <LikeButton
            count={comment.likeCount}
            liked={comment.likedByCurrentUser}
            onClick={() => onLike(comment.id)}
            disabled={disabled}
          />
        </div>
      </div>
    </article>
  );
}

// =============================================================================
// Empty comments state
// =============================================================================

function EmptyCommentsState(): JSX.Element {
  return (
    <div className="comments-empty" role="status" data-testid="comments-empty">
      <p className="comments-empty-text">{EMPTY_COMMENTS_MESSAGE}</p>
    </div>
  );
}

// =============================================================================
// Submission failure region — role=alert for screen readers
// =============================================================================

interface SubmissionErrorProps {
  hasFailed: boolean;
}

function SubmissionError({ hasFailed }: SubmissionErrorProps): JSX.Element | null {
  if (!hasFailed) return null;
  return (
    <div
      className="comments-submit-error"
      role="alert"
      data-testid="composer-error"
    >
      {SUBMIT_ERROR_MESSAGE}
    </div>
  );
}

// =============================================================================
// Composer
// =============================================================================

interface ComposerProps {
  draft: string;
  isSubmitting: boolean;
  hasFailed: boolean;
  currentUser: AuthorSummary | null;
  onDraftChange: (draft: string) => void;
  onSend: () => void;
}

function Composer({
  draft,
  isSubmitting,
  hasFailed,
  currentUser,
  onDraftChange,
  onSend,
}: ComposerProps): JSX.Element {
  const initials = currentUser?.initials ?? '?';
  const trimmedLength = draft.trim().length;
  const sendDisabled = isSubmitting || trimmedLength === 0;

  return (
    <div className="comments-composer" data-testid="comments-composer">
      <SubmissionError hasFailed={hasFailed} />
      <div className="comments-composer-inner">
        <Avatar
          initials={initials}
          className="comments-composer-avatar"
          sizeClass=""
        />
        <input
          type="text"
          className="comments-composer-input"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder={COMPOSER_PLACEHOLDER}
          aria-label={COMPOSER_PLACEHOLDER}
          disabled={isSubmitting}
          data-testid="composer-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && trimmedLength > 0) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <button
          type="button"
          className="comments-send-btn"
          onClick={onSend}
          disabled={sendDisabled}
          aria-label={SEND_ARIA_LABEL}
          aria-disabled={sendDisabled}
          data-busy={isSubmitting}
          data-testid="composer-send"
        >
          <svg
            className="comments-send-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Main view
// =============================================================================

export function CommentsView({
  model,
  referenceTime,
  onBack,
  onDraftChange,
  onSend,
  onLike,
}: CommentsViewProps): JSX.Element {
  const { status, rootPost, comments, composer, currentUser } = model;

  return (
    <div className="comments-screen" data-testid="comments-screen">
      {/* Top bar — node 38:2584 */}
      <header className="comments-topbar" role="banner">
        <div className="comments-topbar-inner">
          <button
            type="button"
            className="comments-back"
            onClick={onBack}
            aria-label={BACK_ARIA_LABEL}
            data-testid="comments-back"
          >
            <svg
              className="comments-back-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="comments-title" data-testid="comments-title">
            {SCREEN_TITLE}
          </span>
        </div>
      </header>

      {/* Content area — scrollable */}
      {status === 'loading' && (
        <div className="comments-content">
          <div
            className="comments-loading"
            role="status"
            aria-label={LOADING_ARIA_LABEL}
            data-testid="comments-loading"
          >
            Загрузка...
          </div>
        </div>
      )}

      {status === 'not_found' && (
        <div className="comments-content">
          <div className="comments-error" data-testid="comments-not-found">
            <p className="comments-error-text comments-error-title">{NOT_FOUND_TITLE}</p>
            <p className="comments-error-hint">{NOT_FOUND_HINT}</p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="comments-content">
          {/* Only generic copy. The raw model.error string is intentionally
              not rendered — security & privacy requirement. */}
          <div className="comments-error" role="alert" data-testid="comments-error">
            <p className="comments-error-text comments-error-title">{ERROR_TITLE}</p>
            <p className="comments-error-hint">{ERROR_HINT}</p>
          </div>
        </div>
      )}

      {status === 'ready' && (
        <>
          <div className="comments-content" data-testid="comments-content">
            {rootPost && <RootPostDisplay post={rootPost} referenceTime={referenceTime} />}
            {comments.length === 0 ? (
              <EmptyCommentsState />
            ) : (
              comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onLike={onLike}
                  disabled={false}
                  referenceTime={referenceTime}
                />
              ))
            )}
          </div>

          <Composer
            draft={composer.draft}
            isSubmitting={composer.isSubmitting}
            hasFailed={composer.hasFailed}
            currentUser={currentUser}
            onDraftChange={onDraftChange}
            onSend={onSend}
          />
        </>
      )}
    </div>
  );
}